/**
 * The catalogs of the client's "Matriz Maestra para Reglas de Vacantes"
 * (12-ago-2026).
 *
 * These are SEED values, not the enforced ones. Every list here is loaded into
 * `catalog_items` and edited from Configuración afterwards — that is the whole
 * ask of §10 of the matrix: «No quiero que estas listas críticas queden
 * enterradas en código. Deben poder mantenerse/ampliarse sin tener que rehacer
 * la lógica de la plataforma». What lives in this file is the starting content
 * so an unseeded database is not empty, exactly like `plans.catalog.ts` does
 * for the commercial model.
 *
 * Two things are deliberately NOT here:
 *
 * - **Brands.** The client is explicit: «NO quiero mantener una lista cerrada
 *   hardcodeada como regla de descarte. Debe ser un catálogo
 *   administrable/autocomplete». So the catalog exists and ships empty; it
 *   fills from what companies actually type.
 * - **Any list of nationalities, ages or genders as criteria.** They are not
 *   modelled as vacancy criteria at all (§7 of the matrix: "NO usar como
 *   auto-discard"), and making them unrepresentable beats validating them away.
 */

/** §2 — Departments, and the roles that hang off each one. */
export const MATRIX_DEPARTMENTS: readonly { department: string; roles: readonly string[] }[] = [
	{
		department: 'Executive Management',
		roles: [
			'General Manager',
			'Hotel Manager',
			'Resort Manager',
			'Director of Operations',
			'Operations Manager',
			'Assistant General Manager',
			'Club General Manager',
			'Venue General Manager',
		],
	},
	{
		department: 'Food & Beverage Service',
		roles: [
			'Director of F&B',
			'F&B Manager',
			'Assistant F&B Manager',
			'Restaurant Manager',
			'Assistant Restaurant Manager',
			'Outlet Manager',
			'Bar Manager',
			'Lounge Manager',
			'Beach Club Manager',
			'Restaurant Supervisor',
			'F&B Supervisor',
			'Team Leader',
			'Head Waiter',
			'Captain',
			'Waiter/Waitress',
			'Bartender',
			'Barista',
			'Host/Hostess',
			'Sommelier',
		],
	},
	{
		department: 'Culinary / Kitchen',
		roles: [
			'Executive Chef',
			'Executive Sous Chef',
			'Chef de Cuisine',
			'Head Chef',
			'Sous Chef',
			'Junior Sous Chef',
			'Chef de Partie',
			'Demi Chef de Partie',
			'Commis 1',
			'Commis 2',
			'Commis 3',
			'Pastry Chef',
			'Pastry Sous Chef',
			'Baker',
			'Sushi Chef',
			'Pizza Chef',
			'Butcher',
		],
	},
	{
		department: 'Front Office',
		roles: [
			'Director of Front Office',
			'Front Office Manager',
			'Assistant Front Office Manager',
			'Duty Manager',
			'Front Office Supervisor',
			'Guest Relations Manager',
			'Guest Relations Supervisor',
			'Guest Relations Agent',
			'Receptionist',
			'Concierge',
			'Bell Captain',
			'Bellman',
			'Doorman',
		],
	},
	{
		department: 'Rooms Division',
		roles: [
			'Director of Rooms',
			'Rooms Division Manager',
			'Assistant Rooms Division Manager',
			'Guest Experience Manager',
			'Club Lounge Manager',
		],
	},
	{
		department: 'Housekeeping',
		roles: [
			'Executive Housekeeper',
			'Assistant Executive Housekeeper',
			'Housekeeping Manager',
			'Housekeeping Supervisor',
			'Public Area Supervisor',
			'Room Attendant',
			'Public Area Attendant',
			'Laundry Manager',
			'Laundry Supervisor',
		],
	},
	{
		department: 'Reservations / Revenue',
		roles: [
			'Director of Revenue',
			'Revenue Manager',
			'Assistant Revenue Manager',
			'Reservations Manager',
			'Assistant Reservations Manager',
			'Reservations Supervisor',
			'Reservations Agent',
		],
	},
	{
		department: 'Sales / Business Development',
		roles: [
			'Director of Sales',
			'Director of Sales & Marketing',
			'Sales Manager',
			'Senior Sales Manager',
			'Business Development Manager',
			'Sales Executive',
			'Account Manager',
		],
	},
	{
		department: 'Marketing / Communications',
		roles: [
			'Director of Marketing',
			'Marketing Manager',
			'Digital Marketing Manager',
			'Social Media Manager',
			'Social Media Executive',
			'PR Manager',
			'Communications Manager',
			'Content Creator',
		],
	},
	{
		department: 'Events / Banquets',
		roles: [
			'Director of Events',
			'Events Manager',
			'Banquet Manager',
			'Assistant Banquet Manager',
			'Events Executive',
			'Banquet Supervisor',
			'Wedding / Events Coordinator',
		],
	},
	{
		department: 'Human Resources / Talent',
		roles: [
			'Director of HR',
			'HR Manager',
			'Assistant HR Manager',
			'Talent Acquisition Manager',
			'Talent Acquisition Specialist',
			'HR Executive',
			'HR Coordinator',
			'Learning & Development Manager',
		],
	},
	{
		department: 'Finance',
		roles: [
			'Director of Finance',
			'Financial Controller',
			'Finance Manager',
			'Chief Accountant',
			'Accountant',
			'Accounts Payable',
			'Accounts Receivable',
			'Cost Controller',
			'Income Auditor',
		],
	},
	{
		department: 'Engineering / Maintenance',
		roles: [
			'Director of Engineering',
			'Chief Engineer',
			'Assistant Chief Engineer',
			'Engineering Manager',
			'Maintenance Manager',
			'HVAC Technician',
			'Electrician',
			'Plumber',
			'General Technician',
		],
	},
	{
		department: 'IT',
		roles: [
			'Director of IT',
			'IT Manager',
			'Assistant IT Manager',
			'Systems Administrator',
			'IT Support',
			'Network Engineer',
		],
	},
	{
		department: 'Spa / Wellness / Recreation',
		roles: [
			'Director of Spa',
			'Spa Manager',
			'Assistant Spa Manager',
			'Spa Supervisor',
			'Therapist',
			'Fitness Manager',
			'Personal Trainer',
			'Recreation Manager',
			'Lifeguard',
		],
	},
	{
		department: 'Security',
		roles: [
			'Director of Security',
			'Security Manager',
			'Assistant Security Manager',
			'Security Supervisor',
			'Security Officer',
		],
	},
	{
		department: 'Stewarding',
		roles: ['Chief Steward', 'Assistant Chief Steward', 'Stewarding Supervisor', 'Steward'],
	},
	{
		department: 'Procurement / Stores',
		roles: [
			'Director of Procurement',
			'Procurement Manager',
			'Purchasing Manager',
			'Purchasing Officer',
			'Store Manager',
			'Storekeeper',
		],
	},
];

/** Flat list of every role in the matrix, for the `positions` catalog. */
export const MATRIX_ROLES: readonly string[] = MATRIX_DEPARTMENTS.flatMap((entry) => entry.roles);

/**
 * §3 — the normalized seniority ladder, with what each level means.
 *
 * Same four keys as `SENIORITY_LEVELS` in `vacancy-criteria.dto` (which also
 * doubles as the Private Shortlist category), so the catalog and the enum
 * cannot drift.
 */
export const MATRIX_SENIORITY: readonly { key: string; label: string; use: string }[] = [
	{
		key: 'operational',
		label: 'Operational',
		use: 'Ejecución diaria sin responsabilidad relevante sobre equipo/departamento.',
	},
	{
		key: 'supervisor',
		label: 'Supervisor',
		use: 'Supervisión de turno, estándares o pequeños equipos.',
	},
	{
		key: 'management',
		label: 'Management',
		use: 'Responsabilidad de outlet, función, departamento, personas o resultados.',
	},
	{
		key: 'senior_director',
		label: 'Senior / Director',
		use: 'Dirección estratégica, multi-department, multi-venue, propiedad o P&L relevante.',
	},
];

/**
 * §4 — specializations, grouped by the area they belong to.
 *
 * The grouping is not decoration: «Las especializaciones deben ser
 * contextuales. No mostrar listas de cocina a un Front Office Manager ni
 * sistemas de Rooms a un Chef». The area travels in the item's `metadata` so
 * the form can filter by it.
 */
export const MATRIX_SPECIALIZATIONS: readonly { area: string; values: readonly string[] }[] = [
	{
		area: 'Culinary - Cuisine',
		values: [
			'Italian',
			'French',
			'Mediterranean',
			'Greek',
			'Spanish',
			'Japanese',
			'Sushi',
			'Chinese',
			'Cantonese',
			'Asian',
			'Thai',
			'Indian',
			'Indian Curry',
			'Tandoor',
			'Arabic',
			'Levantine',
			'Middle Eastern',
			'Turkish',
			'Mexican',
			'Latin American',
			'International',
		],
	},
	{
		area: 'Culinary - Technique/Section',
		values: [
			'Grill',
			'Hot Kitchen',
			'Cold Kitchen / Garde Manger',
			'Pastry',
			'Bakery',
			'Pizza',
			'Sushi Section',
			'Butchery',
			'Banquet Kitchen',
			'Fine Dining',
			'High-volume Production',
		],
	},
	{
		area: 'F&B Service',
		values: [
			'Fine Dining',
			'Luxury Hotel',
			'Beach Club',
			'Nightlife/Lounge',
			'High-volume Restaurant',
			'Private Club',
			'Banquets',
			'Specialty Coffee',
			'Wine Service',
		],
	},
	{
		area: 'Front Office / Rooms',
		values: [
			'Luxury Hotel',
			'Resort',
			'VIP / VVIP',
			'Club Lounge',
			'Concierge',
			'Guest Relations',
			'Butler Service',
			'Pre-opening',
		],
	},
	{
		area: 'Sales',
		values: [
			'Corporate',
			'Leisure',
			'MICE',
			'Groups',
			'Travel Trade',
			'Luxury Travel',
			'GCC Market',
			'International Market',
			'Hospitality B2B',
		],
	},
	{
		area: 'Marketing',
		values: [
			'Social Media',
			'Performance Marketing',
			'Content',
			'PR',
			'Influencer Marketing',
			'CRM',
			'Brand Marketing',
			'Hospitality Marketing',
		],
	},
	{
		area: 'Revenue / Reservations',
		values: ['Hotel Revenue', 'Restaurant Reservations', 'Distribution', 'OTA', 'Groups', 'Revenue Strategy'],
	},
	{
		area: 'Spa / Wellness',
		values: ['Luxury Spa', 'Massage', 'Beauty', 'Fitness', 'Wellness', 'Recreation'],
	},
	{
		area: 'Engineering',
		values: ['HVAC', 'Electrical', 'Plumbing', 'Mechanical', 'Kitchen Equipment', 'Pool', 'BMS'],
	},
];

/** §5 — operation / venue types a candidate may have worked in. */
export const MATRIX_OPERATION_TYPES: readonly string[] = [
	'Luxury 5-star Hotel',
	'Luxury Resort',
	'Ultra-luxury Hotel / Resort',
	'Fine Dining Restaurant',
	'Michelin-starred / Michelin Guide environment',
	'Premium Casual Dining',
	'Beach Club',
	'Private Members Club',
	'Lifestyle Hospitality',
	'Nightlife / Lounge',
	'High-volume Restaurant',
	'International Hotel Chain',
	'Independent Luxury Venue',
	'Pre-opening',
	'Multi-venue / Multi-property',
];

/** §6 — systems and software, by the department that uses them. */
export const MATRIX_SYSTEMS: readonly { area: string; values: readonly string[] }[] = [
	{
		area: 'Front Office / Rooms / Reservations',
		values: ['OPERA PMS', 'OPERA Cloud', 'Oracle Hospitality', 'Protel', 'Cloudbeds', 'Mews'],
	},
	{
		area: 'F&B / Restaurants',
		values: ['Oracle MICROS', 'Simphony', 'SevenRooms', 'OpenTable', 'ResDiary', 'Toast POS'],
	},
	{
		area: 'Sales / CRM',
		values: ['Salesforce', 'HubSpot', 'Opera Sales & Catering', 'Delphi / Amadeus Sales & Event Management'],
	},
	{
		area: 'Revenue',
		values: ['IDeaS', 'Duetto', 'STR / CoStar benchmarking', 'Lighthouse', 'RateGain'],
	},
	{
		area: 'Finance / ERP',
		values: ['SAP', 'Oracle', 'Microsoft Dynamics', 'SunSystems', 'QuickBooks'],
	},
	{
		area: 'HR / Talent',
		values: ['Workday', 'SAP SuccessFactors', 'Oracle HCM', 'LinkedIn Recruiter', 'ATS platforms'],
	},
	{
		area: 'Engineering',
		values: ['BMS platforms', 'CMMS / maintenance systems'],
	},
	{
		area: 'Marketing',
		values: ['Meta Business Suite', 'Google Analytics', 'Google Ads', 'CRM/email marketing platforms'],
	},
];

/**
 * §6 — systems that count as each other.
 *
 * «un sistema equivalente debe poder enviarse a MANUAL REVIEW en vez de
 * descartar automáticamente». This is the table that makes that possible: a
 * candidate who knows the equivalent is not a candidate who fails the
 * requirement, they are one a human should look at.
 */
export const MATRIX_SYSTEM_EQUIVALENCES: readonly { system: string; equivalentTo: string }[] = [
	{ system: 'OPERA Cloud', equivalentTo: 'OPERA PMS' },
	{ system: 'Oracle Hospitality', equivalentTo: 'OPERA PMS' },
	{ system: 'Protel', equivalentTo: 'OPERA PMS' },
	{ system: 'Mews', equivalentTo: 'OPERA PMS' },
	{ system: 'Cloudbeds', equivalentTo: 'OPERA PMS' },
	{ system: 'Simphony', equivalentTo: 'Oracle MICROS' },
	{ system: 'Toast POS', equivalentTo: 'Oracle MICROS' },
	{ system: 'OpenTable', equivalentTo: 'SevenRooms' },
	{ system: 'ResDiary', equivalentTo: 'SevenRooms' },
	{ system: 'Duetto', equivalentTo: 'IDeaS' },
	{ system: 'RateGain', equivalentTo: 'IDeaS' },
	{ system: 'HubSpot', equivalentTo: 'Salesforce' },
	{ system: 'SAP SuccessFactors', equivalentTo: 'Workday' },
	{ system: 'Oracle HCM', equivalentTo: 'Workday' },
];

/** Certifications and licences that can be a real requirement of a role. */
export const MATRIX_CERTIFICATIONS: readonly string[] = [
	'Food Safety / HACCP',
	'PIC (Person in Charge) — Dubai Municipality',
	'Basic Food Hygiene',
	'First Aid',
	'Lifeguard certification',
	'Pool Operator certification',
	'Fire Safety / Fire Warden',
	'WSET (wine)',
	'Sommelier certification',
	'Driving licence',
	'Security guard licence (SIRA)',
	'Electrical licence',
	'HVAC certification',
	'Massage / Spa therapy certification',
	'Personal Trainer certification',
];

/**
 * Seniority level of a role (`MD-Vacante-Modelo-Creacion §I.2`, Jumeirah 13-ago).
 *
 * The company does NOT choose the seniority of its vacancy: it is derived from
 * the position, and this is the table that derives it. It gates publishing —
 * ACCESS may only post Operational, PROFESSIONAL up to Management — so getting a
 * role wrong here is getting somebody's plan wrong.
 *
 * **It is deliberately incomplete.** The client sent a closed table of 35 roles;
 * {@link MATRIX_ROLES} carries ~200 from her own master matrix of 12-ago. The
 * other ~165 stay unclassified on purpose rather than being guessed by us: a
 * role with no level here cannot be published until Kairos assigns one, and that
 * decision is written back to the role in the catalog, so each one costs a
 * human once and never again.
 *
 * Keys are the role labels exactly as they appear in the catalog, because that
 * is what a vacancy stores. `Senior / Director` is written `senior_director`
 * here, matching `SENIORITY_LEVELS`.
 */
export const ROLE_SENIORITY: Readonly<Record<string, string>> = {
	// Operational — daily execution, no relevant responsibility over a team.
	'Waiter / Waitress': 'operational',
	Bartender: 'operational',
	Barista: 'operational',
	'Host / Hostess': 'operational',
	Receptionist: 'operational',
	'Guest Service Agent': 'operational',
	'Commis Chef': 'operational',
	'Room Attendant': 'operational',
	Steward: 'operational',
	Bellman: 'operational',

	// Supervisor — a shift, a standard or a small team.
	'Restaurant Supervisor': 'supervisor',
	'Front Office Supervisor': 'supervisor',
	'Housekeeping Supervisor': 'supervisor',
	'Chef de Partie': 'supervisor',

	// Management — an outlet, a function, a department, people or results.
	'Restaurant Manager': 'management',
	'Front Office Manager': 'management',
	'F&B Manager': 'management',
	'Sales Manager': 'management',
	'Head Chef': 'management',
	'Executive Chef': 'management',
	'Operations Manager': 'management',
	'Commercial Manager': 'management',
	'General Restaurant Manager': 'management',

	// Senior / Director — strategy, multi-department, multi-venue or P&L.
	'General Manager': 'senior_director',
	'Hotel Manager': 'senior_director',
	'Director of Operations': 'senior_director',
	'Director of F&B': 'senior_director',
	'Director of Sales': 'senior_director',
	'Director of Finance': 'senior_director',
	'Cluster Manager': 'senior_director',
	'Cluster Director': 'senior_director',
	'Multi-Venue Manager': 'senior_director',
	'Multi-Property Manager': 'senior_director',
	'Area Manager': 'senior_director',
	'Regional Manager': 'senior_director',
};
